import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useGruposSubgrupos } from './2_crea_grupos_subgrupos';
import { useSeguimientoCambios } from '../hooks/useSeguimientoCambios';
import MenuGruposSubgrupos from '../components/MenuGruposSubgrupos';
import VentanaFlotanteCambios from '../components/VentanaFlotanteCambios';
import formsService from '../utils/formsService';
import ListaPreguntasGrupo from '../components/ListaPreguntasGrupo';
import MENU_CONFIG, { shouldFilterGroupByName } from '../config/config-menu';
import FORM_CONFIG from '../config/formConfig';
import { obtenerPreguntasDeGrupo } from '../utils/gruposSubgruposProcessor';
import { debugLog } from '../utils/debugLog';

/**
 * Paso 3: Generar menú de grupos y subgrupos
 * Este componente se encarga únicamente de la generación y visualización
 * del menú interactivo para navegar por los grupos y subgrupos
 */
const GenerarMenuGrupos = () => {
  // Obtener datos procesados del paso 2
  const { 
    gruposProcesados, 
    estadisticasGrupos, 
    estadoCarga, 
    datosEstanCargados, 
    datosFormulario 
  } = useGruposSubgrupos();

  // Hook para seguimiento de cambios
  const {
    totalCambios,
    establecerValoresOriginales,
    establecerValoresOriginalesParciales,
    registrarCambio,
    obtenerDatosParaEnvio,
    obtenerResumenCambios,
    limpiarCambios,
    tieneCambios
  } = useSeguimientoCambios();

  // Estado para confirmación de cambios no guardados
  const [mostrarConfirmacionCambios, setMostrarConfirmacionCambios] = useState(false);
  const [grupoPendiente, setGrupoPendiente] = useState(null);
  const [enviandoCambios, setEnviandoCambios] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState(null);

  // Estados para el menú de grupos y formulario
  const [grupoSeleccionado, setGrupoSeleccionado] = useState(null);

  // Preguntas estructurales del grupo seleccionado (sin filtrar por relevant/config:
  // ese filtrado real ocurre una sola vez, dentro de GeneradorFormulario)
  const preguntasProcesadas = useMemo(
    () => (grupoSeleccionado ? obtenerPreguntasDeGrupo(grupoSeleccionado) : []),
    [grupoSeleccionado]
  );

  const [preguntaSeleccionada, setPreguntaSeleccionada] = useState(null);
  const [respuestasFormulario, setRespuestasFormulario] = useState({});
  const [rutaNavegacion, setRutaNavegacion] = useState([]);
  const baselineEstablecidoRef = useRef(false);
  
  // Ref del contenedor principal de preguntas para realizar scroll al seleccionar grupo/subgrupo
  const contenidoPrincipalRef = useRef(null);
  
  // Estado del buscador global
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  // Construir un mapa plano de valores por nombre de campo para evaluar relevancias del menú
  const datosParaRelevant = useMemo(() => {
    const map = {};
    const lista = Array.isArray(datosFormulario?.preguntas) ? datosFormulario.preguntas : [];
    lista.forEach((p) => {
      const name = p?.name;
      if (!name) return;
      const hasRespuesta = Object.prototype.hasOwnProperty.call(respuestasFormulario || {}, name);
      if (hasRespuesta) {
        // Usar SIEMPRE la respuesta del usuario, incluso si está vacía, para reflejar el estado actual
        map[name] = respuestasFormulario[name];
        return;
      }
      // Si no hay respuesta del usuario, usar valores por defecto de la pregunta (si existen y no están vacíos)
      const valorDefecto = (p?.config_rpt_valor ?? p?.config_valor ?? p?.valor ?? p?.value);
      if (valorDefecto !== undefined && valorDefecto !== null && valorDefecto !== '') {
        map[name] = valorDefecto;
      }
    });
    try {
      debugLog('[RELEVANT MAP] parcela_actividades=', map['parcela_actividades'], 'potrero_grupo=', map['potrero_grupo']);
    } catch {}
    return map;
  }, [datosFormulario?.preguntas, respuestasFormulario]);

  // Resetear baseline cuando cambia el grupo seleccionado
  useEffect(() => {
    baselineEstablecidoRef.current = false;
  }, [grupoSeleccionado]);

  // Al cambiar de grupo/subgrupo, desplazar la vista hacia arriba (configurable y robusto)
  useEffect(() => {
    if (!grupoSeleccionado) return;
    const navigation = MENU_CONFIG?.navigation || {};
    const enableScroll = navigation.scrollToTopOnGroupSelect;
    if (!enableScroll) return;

    const behavior = navigation.scrollBehavior === 'instant' ? 'auto' : 'smooth';
    const delay = Number(navigation.scrollAfterMs) || 0;
    const offset = Number(navigation.scrollOffset) || 0;
    const target = navigation.scrollTarget || 'auto'; // 'auto' | 'window' | 'container'
    const selector = navigation.scrollContainerSelector || null;

    const doScroll = () => {
      const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
      if (!isBrowser) return;

      const getScrollableParent = (node) => {
        if (!node) return null;
        let parent = node.parentElement;
        while (parent) {
          const style = window.getComputedStyle(parent);
          const overflowY = style.overflowY;
          const canScroll = (overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight;
          if (canScroll) return parent;
          parent = parent.parentElement;
        }
        return null;
      };

      let scrollContainer = null;
      if (target === 'window') {
        scrollContainer = 'window';
      } else if (target === 'container' && contenidoPrincipalRef.current) {
        scrollContainer = contenidoPrincipalRef.current;
      } else if (selector) {
        scrollContainer = document.querySelector(selector) || null;
      } else {
        // auto: intentar el contenedor scrollable más cercano, si no, ventana
        scrollContainer = getScrollableParent(contenidoPrincipalRef.current) || 'window';
      }

      try {
        if (scrollContainer === 'window') {
          window.scrollTo({ top: offset, behavior });
        } else if (scrollContainer && typeof scrollContainer.scrollTo === 'function') {
          scrollContainer.scrollTo({ top: offset, behavior });
        } else if (scrollContainer) {
          // Fallback si no soporta scrollTo
          scrollContainer.scrollTop = offset;
        }

        // Además, procurar que el inicio del contenido quede visible
        const el = contenidoPrincipalRef.current;
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior, block: 'start' });
        }
      } catch (e) {
        // Fallback final: ventana
        try {
          window.scrollTo({ top: 0, behavior });
        } catch {}
      }
    };

    if (delay > 0) {
      const t = setTimeout(() => doScroll(), delay);
      return () => clearTimeout(t);
    } else {
      // Usar RAF para ejecutar tras el siguiente repintado
      const r = requestAnimationFrame(() => doScroll());
      return () => cancelAnimationFrame(r);
    }
  }, [grupoSeleccionado]);

  // Establecer baseline de valores originales del hook PADRE cuando se tengan preguntas procesadas
  useEffect(() => {
    if (!grupoSeleccionado) return;

    // Si el baseline ya está establecido, no modificarlo con entradas locales
    if (baselineEstablecidoRef.current) {
      return;
    }

    const baseline = {};
    const listaPreguntas = preguntasProcesadas || [];

    listaPreguntas.forEach(p => {
      // Baseline debe usar únicamente valores por defecto de la pregunta (config_rpt_valor)
      if (p.config_rpt_valor !== undefined && p.config_rpt_valor !== null && p.config_rpt_valor !== '') {
        baseline[p.name] = p.config_rpt_valor;
      }
    });

    establecerValoresOriginales(baseline);
    baselineEstablecidoRef.current = true;
  }, [grupoSeleccionado, preguntasProcesadas, establecerValoresOriginales]);

  /**
   * Función para enviar cambios al servidor
   */
  const enviarCambios = useCallback(async (datosParaEnvio) => {
    try {
      // // debugLog('📤 [ENVÍO] Enviando cambios al servidor:', datosParaEnvio);

      // Usar el servicio saveGroupData
      const respuesta = await formsService.saveGroupData(datosParaEnvio);

      // Tras guardar exitosamente, actualizar baseline con los valores NUEVOS de los campos modificados
      try {
        const resumen = obtenerResumenCambios();
        if (Array.isArray(resumen) && resumen.length > 0) {
          const nuevosBaseline = {};
          resumen.forEach((cambio) => {
            nuevosBaseline[cambio.campo] = cambio.valorNuevo;
          });
          // Sobrescribir existentes para que futuros cambios comparen contra lo último guardado
          establecerValoresOriginalesParciales(nuevosBaseline, { sobrescribirExistentes: true });
        }
      } catch (_) {
        // No bloquear por errores al actualizar baseline
      }

      // // debugLog('✅ [ENVÍO] Cambios enviados exitosamente:', respuesta);
      return respuesta;
    } catch (error) {
      // console.error('❌ [ENVÍO] Error al enviar cambios:', error);
      throw error;
    }
  }, [obtenerResumenCambios, establecerValoresOriginalesParciales]);

  /**
   * Función para manejar cambios en las respuestas del formulario
   */
  const manejarCambioRespuesta = useCallback((nombreCampo, nuevoValor, pregunta) => {
    debugLog('🔄 [DEBUG] manejarCambioRespuesta ejecutado:', {
      nombreCampo,
      nuevoValor,
      pregunta: pregunta?.label || 'Sin label',
      tipo: pregunta?.type
    });
    
    setRespuestasFormulario(prev => ({
      ...prev,
      [nombreCampo]: nuevoValor
    }));
    
    debugLog('📊 [DEBUG] Llamando a registrarCambio...');
    registrarCambio(nombreCampo, nuevoValor, pregunta);
    
    debugLog('📊 [DEBUG] Estado después del cambio:', {
      totalCambios,
      tieneCambios
    });
  }, [registrarCambio, totalCambios, tieneCambios]);

  /**
   * Maneja la selección de un grupo desde el menú
   */
  const seleccionarGrupoDefinitivo = (grupo) => {
    // Al cambiar de grupo, limpiar la selección de pregunta específica
    setPreguntaSeleccionada(null);
    setGrupoSeleccionado(grupo);
    // debugLog('🎯 [MENU] Grupo seleccionado:', grupo);
  };

  const manejarSeleccionGrupo = (grupo) => {
    if (tieneCambios) {
      // Hay cambios sin enviar: pedir confirmación
      setGrupoPendiente(grupo);
      setMostrarConfirmacionCambios(true);
      return;
    }
    seleccionarGrupoDefinitivo(grupo);
  };

  // Revertir cambios locales a los valores originales del baseline
  const revertirCambiosLocales = useCallback(() => {
    const resumen = obtenerResumenCambios();
    if (resumen && resumen.length > 0) {
      setRespuestasFormulario(prev => {
        const next = { ...prev };
        resumen.forEach(c => {
          next[c.campo] = c.valorOriginal;
        });
        return next;
      });
    }
  }, [obtenerResumenCambios]);

  // Confirmación: continuar sin guardar
  const handleContinuarSinGuardar = () => {
    // Revertir cambios y navegar al grupo pendiente
    revertirCambiosLocales();
    limpiarCambios();
    setMostrarConfirmacionCambios(false);
    if (grupoPendiente) {
      seleccionarGrupoDefinitivo(grupoPendiente);
      setGrupoPendiente(null);
    }
  };

  // Confirmación: enviar cambios
  const handleEnviarCambios = async () => {
    try {
      setEnviandoCambios(true);
      setErrorEnvio(null);
      const datosParaEnvio = obtenerDatosParaEnvio(formsService.getInfoGeneral());
      await enviarCambios(datosParaEnvio);
      limpiarCambios();
      setMostrarConfirmacionCambios(false);
      if (grupoPendiente) {
        seleccionarGrupoDefinitivo(grupoPendiente);
        setGrupoPendiente(null);
      }
    } catch (err) {
      const status = err?.status || err?.response?.status;
      const serverMessage = err?.serverMessage || err?.response?.data?.message;
      const texto = err?.message || (status ? `Error (${status})` : 'Error al enviar los cambios');
      setErrorEnvio({ texto, detalles: serverMessage });
    } finally {
      setEnviandoCambios(false);
    }
  };

  /**
   * Maneja los cambios en la ruta de navegación
   */
  const manejarCambioRutaNavegacion = (nuevaRuta) => {
    setRutaNavegacion(nuevaRuta);
  };

  // Construye la ruta completa hacia un elemento (grupo/subgrupo)
  const construirRutaElemento = useCallback((elementoObjetivo) => {
    if (!gruposProcesados || gruposProcesados.length === 0 || !elementoObjetivo) return [];

    const encontrarCamino = (elementos, objetivo, caminoActual = []) => {
      for (const item of elementos) {
        const nuevoCamino = [...caminoActual, item];
        if (item.id === objetivo.id) {
          return nuevoCamino;
        }
        if (item.subgrupos && item.subgrupos.length > 0) {
          const resultado = encontrarCamino(item.subgrupos, objetivo, nuevoCamino);
          if (resultado) return resultado;
        }
        if (item.gruposHijos && item.gruposHijos.length > 0) {
          const resultado = encontrarCamino(item.gruposHijos, objetivo, nuevoCamino);
          if (resultado) return resultado;
        }
      }
      return null;
    };

    const camino = encontrarCamino(gruposProcesados, elementoObjetivo);
    return camino || [];
  }, [gruposProcesados]);

  // Búsqueda global de preguntas en toda la estructura
  const performGlobalSearch = useCallback((term) => {
    const t = term.trim();
    if (!t || t.length < 2) {
      setSearchResults([]);
      return;
    }
    const lower = t.toLowerCase();
    const filters = (FORM_CONFIG && FORM_CONFIG.search) ? FORM_CONFIG.search : {};
    const excludeTypes = (filters.excludeSearchByType || []).map(x => String(x).toLowerCase());
    const excludeNames = (filters.excludeSearchByName || []).map(x => String(x).toLowerCase());
    const excludeLabels = (filters.excludeSearchByLabel || []).map(x => String(x).toLowerCase());
    const resultados = [];

    const recorrer = (elementos, camino = []) => {
      elementos.forEach((el) => {
        // Omitir elementos/grupos ocultos por configuración de nombre
        if (shouldFilterGroupByName(el)) {
          return;
        }
        const nuevoCamino = [...camino, el];
        if (el.preguntas && Array.isArray(el.preguntas)) {
          el.preguntas.forEach((pregunta) => {
            const label = (pregunta.label || '').toLowerCase();
            const name = (pregunta.name || '').toLowerCase();
            // Determinar tipo base (e.g., 'select_one list' => 'select_one')
            const tipoBase = String(pregunta.type || '').split(' ')[0].toLowerCase();

            // Aplicar filtros de exclusión por tipo/nombre/label
            const tipoExcluido = excludeTypes.includes(tipoBase);
            const nombreExcluido = excludeNames.length > 0 && name && excludeNames.includes(name);
            const labelExcluido = excludeLabels.length > 0 && label && excludeLabels.some(l => label.includes(l));

            if (tipoExcluido || nombreExcluido || labelExcluido) {
              return; // saltar esta pregunta
            }

            // Política de visibilidad: excluir de búsqueda preguntas no actualizables (config_actualizable = 0/false)
            try {
              const hideNotUpdatable = FORM_CONFIG?.visibilityPolicies?.hideNotUpdatable?.enabled === true;
              const isNotUpdatable = (pregunta.config_actualizable === false || pregunta.config_actualizable === 0 || pregunta.config_actualizable === '0');
              if (hideNotUpdatable && isNotUpdatable) {
                return; // excluir esta pregunta del buscador
              }
            } catch (e) {
              // Ignorar errores de config y continuar
            }

            if ((label && label.includes(lower)) || (name && name.includes(lower))) {
              const ruta = nuevoCamino.map((e) => e.label || e.name || e.nombre || 'Sin título').join(' → ');
              resultados.push({ pregunta, elemento: el, ruta });
            }
          });
        }
        if (el.subgrupos && el.subgrupos.length > 0) {
          recorrer(el.subgrupos, nuevoCamino);
        }
        if (el.gruposHijos && el.gruposHijos.length > 0) {
          recorrer(el.gruposHijos, nuevoCamino);
        }
      });
    };

    recorrer(gruposProcesados, []);
    setSearchResults(resultados);
  }, [gruposProcesados]);

  const handleSearchChange = useCallback((e) => {
    const value = typeof e === 'string' ? e : e?.target?.value || '';
    setSearchTerm(value);
    performGlobalSearch(value);
  }, [performGlobalSearch]);

  const handleSearchResultClick = useCallback((resultado) => {
    if (!resultado || !resultado.elemento) return;
    // Seleccionar directamente la pregunta encontrada y el grupo contenedor
    setPreguntaSeleccionada(resultado.pregunta);
    manejarSeleccionGrupo(resultado.elemento);
    setSearchTerm('');
    setSearchResults([]);
  }, [manejarSeleccionGrupo]);

  /**
   * Renderiza la ruta de navegación (breadcrumb)
   */
  const renderizarRutaNavegacion = () => {
    // debugLog('🔍 [PADRE] Renderizando ruta de navegación:', rutaNavegacion);
    
    if (rutaNavegacion.length === 0) {
      return null;
    }

    return (
      <div className="form-master-ruta-navegacion mb-3">
        <span className="form-master-ruta-label">Ruta:</span>
        {rutaNavegacion.map((elemento, index) => {
          // debugLog('🔍 [PADRE] Elemento en ruta:', elemento);
          const titulo = elemento.label || elemento.name || elemento.nombre || elemento.titulo || 'Sin título';
          // debugLog('🔍 [PADRE] Título obtenido:', titulo);
          
          return (
            <React.Fragment key={elemento.id || index}>
              <span className="form-master-ruta-elemento">
                {elemento.tipo === 'grupo' ? '📁' : elemento.tipo === 'subgrupo' ? '📂' : '❓'} {titulo}
              </span>
              {index < rutaNavegacion.length - 1 && (
                <span className="form-master-ruta-separador"> → </span>
              )}
            </React.Fragment>
          );
        })}
      </div>
     );
   };

   /**
   * Maneja las respuestas del formulario
   * Optimizado para evitar bucles infinitos
   */
  const manejarRespuestaFormulario = useCallback((respuestas) => {
    setRespuestasFormulario(prev => {
      const nuevasRespuestas = {
        ...prev,
        ...respuestas
      };
      
      // debugLog('📝 [MENU] Respuestas actualizadas:', respuestas);
      
      return nuevasRespuestas;
    });
  }, []);

  // Si no hay grupos, mostrar mensaje informativo
  if (!gruposProcesados || gruposProcesados.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h3>Paso 3: Generar Menú de Grupos</h3>
        <p>⏳ No hay grupos disponibles para mostrar</p>
        <p>Asegúrate de que el paso anterior haya procesado correctamente los grupos.</p>
      </div>
    );
  }

  // Exportar funciones y componentes para uso externo
  return {
    // Componente principal del menú
    Componente: () => (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Buscador global */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="text"
                className="form-control"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Buscar preguntas en todos los grupos..."
                style={{ maxWidth: '520px' }}
              />
              {searchTerm && (
                <span style={{ fontSize: '12px', color: '#666' }}>
                  {searchResults.length} resultado(s)
                </span>
              )}
            </div>
            {searchTerm && searchResults.length > 0 && (
              <div style={{
                backgroundColor: '#fff',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                padding: '8px',
                marginTop: '8px',
                maxHeight: '220px',
                overflowY: 'auto'
              }}>
                {searchResults.slice(0, 20).map((res, idx) => (
                  <div
                    key={`${res.pregunta?.name || 'preg'}-${idx}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '6px 8px',
                      borderBottom: '1px dashed #f1f3f5',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleSearchResultClick(res)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleSearchResultClick(res);
                      }
                    }}
                    title={res.ruta}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {res.pregunta?.label || res.pregunta?.name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {res.ruta}
                      </div>
                    </div>
                    {/* Eliminado el botón "Abrir grupo". Ahora el item completo es clickeable */}
                  </div>
                ))}
                {searchResults.length > 20 && (
                  <div style={{ fontSize: '12px', color: '#666', padding: '4px 8px' }}>
                    Mostrando 20 de {searchResults.length} resultados. Afina tu búsqueda.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Renderizar la ruta de navegación fuera del menú */}
          {renderizarRutaNavegacion()}

          {/* Layout principal con menú y contenido usando Bootstrap */}
          <div className="row">
            {/* Columna 1: Menú de grupos - col-md-4 */}
            <div className="col-md-4">
              <MenuGruposSubgrupos
                grupos={gruposProcesados}
                grupoSeleccionado={grupoSeleccionado}
                onSeleccionGrupo={manejarSeleccionGrupo}
                onRutaNavegacionChange={manejarCambioRutaNavegacion}
                datosFormulario={datosParaRelevant}
              />
            </div>

            {/* Columna 2: Contenido principal - col-md-8 */}
            <div className="col-md-8" ref={contenidoPrincipalRef}>
              {/* Lista de preguntas del grupo seleccionado */}
              <ListaPreguntasGrupo
                grupoSeleccionado={grupoSeleccionado}
                datosFormulario={datosParaRelevant}
                respuestas={respuestasFormulario}
                respuestasFormulario={respuestasFormulario}
                onRespuestasChange={manejarRespuestaFormulario}
                onCambioRespuesta={manejarCambioRespuesta}
                rutaNavegacion={rutaNavegacion}
                preguntasProcesadas={preguntasProcesadas}
                preguntaSeleccionada={preguntaSeleccionada}
              />
            </div>
          </div>

          {/* Ventana flotante de cambios */}
          {debugLog('🔍 [DEBUG] Estado VentanaFlotanteCambios:', {
            tieneCambios,
            totalCambios,
            visible: tieneCambios
          })}

          <VentanaFlotanteCambios
            totalCambios={totalCambios}
            obtenerResumenCambios={obtenerResumenCambios}
            obtenerDatosParaEnvio={obtenerDatosParaEnvio}
            infoGeneral={formsService.getInfoGeneral()}
            onEnviarCambios={enviarCambios}
            // Tras enviar exitosamente, sólo limpiar el seguimiento, NO revertir valores
            onLimpiarCambios={limpiarCambios}
            // Al presionar "Descartar", revertir al baseline y limpiar el seguimiento
            onDescartarCambios={() => {
              try { revertirCambiosLocales(); } catch {}
              try { limpiarCambios(); } catch {}
            }}
            visible={tieneCambios}
          />

          {mostrarConfirmacionCambios && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#fff', borderRadius: '8px', maxWidth: '520px', width: '90%', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h5 style={{ margin: 0 }}>Hay preguntas por guardar</h5>
                </div>
                <div style={{ padding: '16px 20px' }}>
                  <p>Has realizado cambios que aún no has enviado.</p>
                  {errorEnvio && (
                    <div>
                      <div style={{ color: '#b00020', marginTop: '8px' }}>{typeof errorEnvio === 'string' ? errorEnvio : errorEnvio.texto}</div>
                      {errorEnvio.detalles && (
                        <div style={{ marginTop: 6 }}>
  
                          {Array.isArray(errorEnvio.detalles?.errores) && errorEnvio.detalles.errores.length > 0 && (
                            <div style={{ fontSize: '0.9em', marginTop: 4 }}>
                              <hr />
                              <div style={{ fontWeight: 'bold' }}>Detalles de errores:</div>
                              {errorEnvio.detalles.errores.map((e, idx) => (
                                <div key={`${e.campo}-${idx}`} style={{ marginTop: 4 }}>
                                  <div>
                                    <strong>{e.campo}</strong>
                                  </div>
                                  <div style={{ color: '#b00020' }}>{e.error}</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ padding: '16px 20px', borderTop: '1px solid #eee', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button className="btn btn-outline-secondary" onClick={handleContinuarSinGuardar} disabled={enviandoCambios}>
                    Continuar sin guardar
                  </button>
                  <button className="btn btn-primary" onClick={handleEnviarCambios} disabled={enviandoCambios}>
                    {enviandoCambios ? 'Enviando...' : 'Enviar Cambios'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    ),
    
    // Datos y funciones del menú
    estadisticas: estadisticasGrupos,
    grupoSeleccionado,
    respuestasFormulario,

    // Datos del grupo seleccionado
    preguntasProcesadas,

    // Funciones de manejo
    manejarSeleccionGrupo,
    manejarRespuestaFormulario,

    // Hook personalizado para uso externo
    hook: {
      grupoSeleccionado,
      setGrupoSeleccionado,
      respuestasFormulario,
      setRespuestasFormulario,
      estadisticasGrupos,
      manejarSeleccionGrupo,
      manejarRespuestaFormulario,
      preguntasProcesadas
    }
  };
};

// Exportar el componente principal como default
export default function GenerarMenuGruposMain(props) {
  const resultado = GenerarMenuGrupos(props);
  return resultado.Componente();
}

// Exportar también la función principal
export { GenerarMenuGrupos };